import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { Asset } from 'contentful'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { VideoBlock } from '@/atoms/VideoBlock'
import ContentfulImage from '@/molecules/ContentfulImage'
import { IMediaGalleryItemFields } from '@/types/generated-contentful'
import MediaGalleryItemDetails from './MediaGalleryItemDetails'

export interface Props extends IMediaGalleryItemFields {
  containerWidth: number
  containerHeight: number
  showDetails: boolean
  mouseHandlers: { onMouseOver: () => void; onMouseOut: () => void }
}

export const getDimensions = ({
  image,
  aspectRatio,
  containerWidth,
  containerHeight,
}: {
  image?: Asset
  aspectRatio?: number
  containerWidth: number
  containerHeight: number
}) => {
  const { width = 1, height = 1 } = image?.fields.file.details.image || {}

  const r = aspectRatio || width / height
  const isLandscape = r >= 1

  return {
    width: isLandscape ? containerWidth : containerHeight * r,
    height: isLandscape ? containerWidth / r : containerHeight,
  }
}

const MediaGalleryItem: React.FC<Props> = ({
  image,
  useBlurredImageBackground,
  video,
  title,
  caption,
  containerWidth,
  containerHeight,
  showDetails,
  mouseHandlers,
}) => {
  const [loaded, setLoaded] = useState(false)

  const { width, height } = useMemo(
    () =>
      getDimensions({
        image,
        aspectRatio: video?.fields.aspectRatio,
        containerWidth,
        containerHeight,
      }),
    [image, video, containerWidth, containerHeight],
  )

  return (
    <div className="relative flex h-full w-full shrink-0 snap-center snap-always items-center justify-center overflow-hidden">
      {useBlurredImageBackground && (
        <div className="absolute inset-0 -m-6">
          <ContentfulImage
            image={image}
            className={classNames(
              'transition-opacity ease-in-out duration-500 w-full h-full object-cover opacity-25 pointer-events-none',
              { 'opacity-0': !loaded },
            )}
            containerClassName="h-full"
            width={100}
            height={100}
            quality={50}
            style={{ filter: 'blur(20px)' }}
          />
        </div>
      )}

      <div className={classNames('relative z-10', { 'w-full': !!video })}>
        {video && (
          <VideoBlock
            allow="autoplay"
            {...video.fields}
            className={classNames('w-full transition-opacity duration-500 delay-500', {
              'opacity-0': !loaded,
            })}
            onLoad={() => setLoaded(true)}
          />
        )}

        {!video && (
          <ContentfulImage
            image={image}
            className={classNames('transition-opacity ease-in-out duration-500', { 'opacity-0': !loaded })}
            width={width}
            height={height}
            fit="fill"
            focus="face"
            lazy
            onLoad={() => setLoaded(true)}
          />
        )}

        <MediaGalleryItemDetails title={title} caption={caption} className="p-4 text-white lg:hidden" />
      </div>

      <div
        className={classNames(
          'hidden lg:block absolute bottom-0 left-0 right-0 p-4 md:p-6 lg:p-8 pb-20 lg:pb-8 pt-48 text-white z-20 pointer-events-none',
          'transition-opacity ease-in-out duration-500 delay-500',
          'bg-gradient-to-t from-[rgba(0,0,0,1)] to-[rgba(0,0,0,0)]',
          { 'opacity-0': !showDetails },
        )}
        {...mouseHandlers}
      >
        <MediaGalleryItemDetails
          title={title}
          caption={caption}
          className={classNames('transition duration-500 pointer-events-auto delay-500', {
            'translate-y-full': !showDetails,
          })}
        />
      </div>

      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinnerIcon size={50} />
        </div>
      )}
    </div>
  )
}

export default MediaGalleryItem
